// Warehouse structure — data access (sites, warehouses, locations).
//
// Queries go straight to the RLS-protected tables through PostgREST:
//   sites       sites?select=…&order=code                       (a handful per tenant)
//   warehouses  warehouses?select=…,site,company&order=code,id  (dozens per tenant, one page)
//               index: warehouses_tenant_company_code_key; seq scan is cheaper below ~1k rows
//   locations   locations?warehouse_id=eq.X&code=gt.<cursor>&order=code&limit=51
//               index: locations_tenant_warehouse_code_key (tenant_id, warehouse_id, code)
//               0.4 ms at 60k locations in one warehouse. Prefix search is a
//               range on the same index: code=gte.A-01&code=lt.A-02 (code is
//               collate "C"; LIKE would not use the index under RLS).
//   bulk create rpc create_locations(warehouse, codes[], zone, flags) — one statement
//   writes      plain insert/update; RLS + column grants enforce permissions and
//               immutable codes/parents; audit rows come from triggers.

import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import type { Page } from "@/components/DataList";
import { isDemoMode, supabase } from "@/lib/supabase";
import { demoApi } from "./demo";
import type { ListFilter, ListSort } from "@patronix/domain/list";
import { applyRestFilters, applyRestSort, restCursorOf, type RestColumn, type RestCursor } from "@/lib/restList";

export const warehouseTypes = ["main", "production", "quarantine", "transit", "subcontractor"] as const;
export type WarehouseType = (typeof warehouseTypes)[number];

export interface Site {
  id: string;
  company_id: string;
  code: string;
  name: string;
  is_active: boolean;
}

export interface WarehouseRow {
  id: string;
  company_id: string;
  site_id: string;
  code: string;
  name: string;
  warehouse_type: WarehouseType;
  allow_negative: boolean;
  is_nettable: boolean;
  is_active: boolean;
  /** Quality (R1): where stock waiting for an inspection decision is put. */
  quarantine_location_id: string | null;
  /** Subcontractor warehouses: the supplier holding the stock. */
  supplier_id: string | null;
  supplier: { code: string; name: string } | null;
  updated_at: string;
  site: { code: string; name: string } | null;
  quarantine_location: { code: string } | null;
  company: { code: string } | null;
}

export interface WarehouseInput {
  company_id: string;
  site_id: string;
  code: string;
  name: string;
  warehouse_type: WarehouseType;
  allow_negative: boolean;
  supplier_id: string | null;
}

export type WarehouseUpdate = Pick<
  WarehouseRow,
  "name" | "warehouse_type" | "allow_negative" | "is_nettable" | "is_active" | "quarantine_location_id" | "supplier_id"
>;

export interface LocationRow {
  id: string;
  warehouse_id: string;
  code: string;
  description: string | null;
  zone: string | null;
  is_receivable: boolean;
  is_pickable: boolean;
  is_blocked: boolean;
  is_default: boolean;
  is_active: boolean;
  updated_at: string;
}

export type LocationInput = Pick<LocationRow, "code" | "description" | "zone" | "is_receivable" | "is_pickable" | "is_blocked">;
export type LocationUpdate = Omit<LocationInput, "code"> & { is_active: boolean };

export interface LocationListParams {
  warehouseId: string;
  q: string;
  inactive: boolean;
  /** Column filters and sort (locationListColumns). */
  filters?: ListFilter[];
  sort?: ListSort | null;
}

const locationRestColumns: RestColumn[] = [
  { key: "code", column: "code", type: "text" },
  { key: "description", column: "description", type: "text", nullable: true },
  { key: "zone", column: "zone", type: "text", nullable: true },
];
const locationDefaultSort: ListSort = { col: "code", dir: "asc" };

export const LOCATION_PAGE = 50;
const WAREHOUSE_LIMIT = 500;

const warehouseColumns =
  "id,company_id,site_id,code,name,warehouse_type,allow_negative,is_nettable,is_active,quarantine_location_id,supplier_id,updated_at," +
  "site:sites!warehouses_site_fkey(code,name),company:companies!warehouses_company_fkey(code)," +
  "quarantine_location:locations!warehouses_quarantine_location_fkey(code)," +
  "supplier:business_partners!warehouses_supplier_fkey(code,name)";
const locationColumns = "id,warehouse_id,code,description,zone,is_receivable,is_pickable,is_blocked,is_default,is_active,updated_at";

function db() {
  if (!supabase) throw new Error("Supabase is not configured");
  return supabase;
}

/** Location codes contain only A–Z 0–9 . / -; anything else cannot match. */
export function normalizeLocationQuery(q: string): string {
  return q.toUpperCase().replace(/[^A-Z0-9./-]/g, "");
}

/** Half-open byte-order range [prefix, upper) matching every code that starts
 *  with prefix. Codes are ASCII and sorted with collate "C". */
export function prefixRange(prefix: string): { from: string; to: string } {
  const last = prefix.charCodeAt(prefix.length - 1);
  return { from: prefix, to: prefix.slice(0, -1) + String.fromCharCode(last + 1) };
}

async function listSites(signal: AbortSignal): Promise<Site[]> {
  if (isDemoMode) return demoApi.listSites();
  const { data, error } = await db().from("sites").select("id,company_id,code,name,is_active").order("code").abortSignal(signal);
  if (error) throw error;
  return data as Site[];
}

async function listWarehouses(inactive: boolean, signal: AbortSignal): Promise<Page<WarehouseRow>> {
  if (isDemoMode) return demoApi.listWarehouses(inactive);
  let query = db().from("warehouses").select(warehouseColumns).order("code").order("id").limit(WAREHOUSE_LIMIT).abortSignal(signal);
  if (!inactive) query = query.eq("is_active", true);
  const { data, error } = await query;
  if (error) throw error;
  return { rows: data as unknown as WarehouseRow[], nextCursor: null };
}

async function getWarehouse(id: string, signal: AbortSignal): Promise<WarehouseRow | null> {
  if (isDemoMode) return demoApi.getWarehouse(id);
  const { data, error } = await db().from("warehouses").select(warehouseColumns).eq("id", id).abortSignal(signal).maybeSingle();
  if (error) throw error;
  return data as unknown as WarehouseRow | null;
}

async function listLocations(p: LocationListParams, cursor: string | null, signal: AbortSignal): Promise<Page<LocationRow>> {
  if (isDemoMode) return demoApi.listLocations(p, cursor);
  // Default: by code (locations_warehouse_code index); the search box is a
  // code prefix range; column filters / sorts on the warehouse's locations.
  let query = db().from("locations").select(locationColumns).eq("warehouse_id", p.warehouseId);
  const prefix = normalizeLocationQuery(p.q);
  if (prefix) {
    const range = prefixRange(prefix);
    query = query.gte("code", range.from).lt("code", range.to);
  }
  if (!p.inactive) query = query.eq("is_active", true);
  query = applyRestFilters(query, locationRestColumns, p.filters ?? []);
  query = applyRestSort(query, locationRestColumns, p.sort ?? null, locationDefaultSort, cursor ? (JSON.parse(cursor) as RestCursor) : null);
  const { data, error } = await query.limit(LOCATION_PAGE + 1).abortSignal(signal);
  if (error) throw error;
  const rows = data as LocationRow[];
  const hasMore = rows.length > LOCATION_PAGE;
  const page = hasMore ? rows.slice(0, LOCATION_PAGE) : rows;
  const last = page[page.length - 1];
  return {
    rows: page,
    nextCursor: hasMore && last ? restCursorOf(last as unknown as Record<string, unknown>, locationRestColumns, p.sort ?? null, locationDefaultSort) : null,
  };
}

async function getLocation(id: string, signal: AbortSignal): Promise<LocationRow | null> {
  if (isDemoMode) return demoApi.getLocation(id);
  const { data, error } = await db().from("locations").select(locationColumns).eq("id", id).abortSignal(signal).maybeSingle();
  if (error) throw error;
  return data as LocationRow | null;
}

export async function createSite(tenantId: string, input: { company_id: string; code: string; name: string }): Promise<string> {
  if (isDemoMode) return demoApi.createSite(input);
  const { data, error } = await db()
    .from("sites")
    .insert({ tenant_id: tenantId, ...input })
    .select("id")
    .single();
  if (error) throw error;
  return (data as { id: string }).id;
}

export async function createWarehouse(tenantId: string, input: WarehouseInput): Promise<string> {
  if (isDemoMode) return demoApi.createWarehouse(input);
  const { data, error } = await db()
    .from("warehouses")
    .insert({ tenant_id: tenantId, ...input })
    .select("id")
    .single();
  if (error) throw error;
  return (data as { id: string }).id;
}

export async function updateWarehouse(id: string, input: WarehouseUpdate): Promise<void> {
  if (isDemoMode) return demoApi.updateWarehouse(id, input);
  const { error } = await db().from("warehouses").update(input).eq("id", id);
  if (error) throw error;
}

export async function createLocation(tenantId: string, warehouseId: string, input: LocationInput): Promise<string> {
  if (isDemoMode) return demoApi.createLocation(warehouseId, input);
  const { data, error } = await db()
    .from("locations")
    .insert({ tenant_id: tenantId, warehouse_id: warehouseId, ...input })
    .select("id")
    .single();
  if (error) throw error;
  return (data as { id: string }).id;
}

export async function updateLocation(id: string, input: LocationUpdate): Promise<void> {
  if (isDemoMode) return demoApi.updateLocation(id, input);
  const { error } = await db().from("locations").update(input).eq("id", id);
  if (error) throw error;
}

export async function createLocations(
  warehouseId: string,
  codes: string[],
  zone: string | null,
  isReceivable: boolean,
  isPickable: boolean,
): Promise<number> {
  if (isDemoMode) return demoApi.createLocations(warehouseId, codes, zone, isReceivable, isPickable);
  const { data, error } = await db().rpc("create_locations", {
    p_warehouse_id: warehouseId,
    p_codes: codes,
    p_zone: zone,
    p_is_receivable: isReceivable,
    p_is_pickable: isPickable,
  });
  if (error) throw error;
  return data as number;
}

// ---------------------------------------------------------------------------
// Query options
// ---------------------------------------------------------------------------

export const structureKeys = {
  all: ["warehousing", "structure"] as const,
  sites: () => [...structureKeys.all, "sites"] as const,
  warehouses: (inactive: boolean) => [...structureKeys.all, "warehouses", { inactive }] as const,
  warehouse: (id: string) => [...structureKeys.all, "warehouse", id] as const,
  locations: (p: LocationListParams) => [...structureKeys.all, "locations", p] as const,
  location: (id: string) => [...structureKeys.all, "location", id] as const,
};

// Structure is reference data: changes are rare and invalidate on save.
const STALE = 5 * 60_000;

export const siteListQuery = queryOptions({
  queryKey: structureKeys.sites(),
  queryFn: ({ signal }) => listSites(signal),
  staleTime: STALE,
});

export function warehouseListQuery(inactive: boolean) {
  return infiniteQueryOptions({
    queryKey: structureKeys.warehouses(inactive),
    queryFn: ({ signal }) => listWarehouses(inactive, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (last: Page<WarehouseRow>) => last.nextCursor,
    staleTime: STALE,
  });
}

export function warehouseDetailQuery(id: string) {
  return queryOptions({
    queryKey: structureKeys.warehouse(id),
    queryFn: ({ signal }) => getWarehouse(id, signal),
    staleTime: STALE,
  });
}

export function locationListQuery(p: LocationListParams) {
  return infiniteQueryOptions({
    queryKey: structureKeys.locations(p),
    queryFn: ({ pageParam, signal }) => listLocations(p, pageParam, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (last: Page<LocationRow>) => last.nextCursor,
    enabled: !!p.warehouseId,
    staleTime: STALE,
  });
}

export function locationDetailQuery(id: string) {
  return queryOptions({
    queryKey: structureKeys.location(id),
    queryFn: ({ signal }) => getLocation(id, signal),
    staleTime: STALE,
  });
}
